import z from 'zod';
import {
  DEFAULT_LLM_MODEL_ID,
  DEFAULT_LLM_MODEL_TYPE,
  DEFAULT_VECTOR_MATCH_BUTTON_TEXT,
  LLMModelId,
  LLMModelType,
} from '../../../services/llm/llm.type';
import type { POJO } from '../generic/generic';
import type { MainBlock } from './common';

export const DEFAULT_LLM_SCORING_THRESHOLD = 65;
export const DEFAULT_BUTTON_NAVIGATION = true;
export const DEFAULT_STORE_TRANSCRIPT = false;
// 0 means idle timeout is disabled
export const DEFAULT_LLM_IDLE_TIMEOUT = 0;

type TPlatformOptions = {
  typingIndicator?: boolean;
};

const ConversationSchema = z.object({
  type: z.enum(['user', 'assistant', 'system']),
  message: z.string(),
});

export type TConversation = z.infer<typeof ConversationSchema>;

const LLMBlockParamsSchema = z.object({
  botName: z.string(),
  clientName: z.string(),
  scoring: z.number().default(DEFAULT_LLM_SCORING_THRESHOLD),
  dataSources: z.array(z.string()), // this is the AI knowledge sources/ IDs, but kept as dataSources for backward compatibility reasons
  aiKnowledgeLabels: z.array(z.string()).optional(),
  // This flag indicates whether to use AI knowledge labels or sources of data.
  // This is not controlled from LLM Block UI, but from General -> Company features settings.
  // If this company feature is enabled, this flag will be set to true for all LLM Blocks.
  // CORE side doesn't have access to company features directly, so that's why this flag is needed here as well.
  useAIKnowledgeLabels: z.boolean().optional(),
  // Mirrors the AutoApproveReviews company platform feature onto the block so core
  // can read it at runtime without direct access to company features.
  autoApproveReviews: z.boolean().optional(),
  useContext: z.boolean(),
  contextLength: z.number(),
  prompt: z.array(ConversationSchema),
  filters: z.record(z.string(), z.unknown()),
  isOpenPrompt: z.boolean().optional(),
  useKnowledgeArticles: z.boolean().optional(),
  knowledgeArticles: z.array(z.string()).optional(),
  tag: z.string().optional(),
  guardRailsConfig: z
    .object({
      guardRailId: z.string().optional(),
      guardRailVersion: z.string().optional(),
    })
    .optional(),
  shouldGenerateVectorMatchButton: z.boolean().optional(),
  vectorMatchButtonText: z.string().default(DEFAULT_VECTOR_MATCH_BUTTON_TEXT),
  llmType: z.enum(LLMModelType).default(DEFAULT_LLM_MODEL_TYPE),
  modelId: z.enum(LLMModelId).default(DEFAULT_LLM_MODEL_ID),
  storeTranscript: z.boolean().default(DEFAULT_STORE_TRANSCRIPT),
  idleTimeout: z.number().min(0).default(DEFAULT_LLM_IDLE_TIMEOUT),
  config: z
    .object({
      buttonNavigation: z
        .object({
          enabled: z.boolean().default(DEFAULT_BUTTON_NAVIGATION),
          buttonIds: z.string().array().default([]),
        })
        .optional(),
    })
    .default({
      buttonNavigation: {
        enabled: DEFAULT_BUTTON_NAVIGATION,
        buttonIds: [],
      },
    }),
});

type TLLMBlockParams = z.infer<typeof LLMBlockParamsSchema>;

export const LLMBlockSchema = z.object({
  id: z.string(),
  type: z.literal('llm'),
  blockTitle: z.string().optional(),
  subFlow: z.string().optional(),
  revisionId: z.string(),
  params: LLMBlockParamsSchema,
  client: z.string(),
  prepare: z.record(z.string(), z.unknown()).optional(),
  options: z.record(z.string(), z.unknown()).optional(),
  buttons: z.array(z.string()).optional(),
  errorDestination: z.array(z.string()),
  destination: z.array(z.string()),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type LLMBlock = MainBlock & {
  type: 'llm';
  params: TLLMBlockParams;
  client: string;
  prepare?: POJO<string>;
  options?: TPlatformOptions;
  buttons?: string[];
  errorDestination: string[];
  destination: string[];
};

export const LLMBlockWithMetadataSchema = z.object({
  id: z.string(),
  label: z.string(),
  block: LLMBlockSchema,
  revisionName: z.string(),
  subflowName: z.string(),
  createdAt: z.string().optional(),
});

export type TLLMBlockWithMetadata = z.infer<typeof LLMBlockWithMetadataSchema>;

export const LLMBlocksFilterSchema = z.object({
  id: z.string(),
  botName: z.string(),
});

export type TLLMBlocksFilter = z.infer<typeof LLMBlocksFilterSchema>;

export enum LLMBlockTargetField {
  DataSources = 'dataSources',
  KnowledgeArticles = 'knowledgeArticles',
}
